import React from 'react';
import { ArrowLeft, Shield, Eye, Database, Clock, Users, Mail, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/40 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/auth" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              BillPortal
            </span>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Title */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-muted-foreground">
            Effective Date: January 26, 2025 | Version 1.0
          </p>
        </div>

        {/* Introduction */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Your Privacy Matters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              At BillPortal, we are committed to protecting your privacy and ensuring the security of your personal 
              information. This Privacy Policy explains how we collect, use, protect, and share your information when 
              you use our bill processing and management platform.
            </p>
            <p>
              BillPortal is an invitation-only enterprise platform designed for secure document processing and 
              workflow management. We implement industry-leading security measures to protect your data.
            </p>
          </CardContent>
        </Card>

        {/* Data Collection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Information We Collect
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Account and Profile Information</h4>
                <p className="text-muted-foreground">
                  Name, email address, company information, and workspace details provided during 
                  invitation acceptance and account setup.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Document and Bill Data</h4>
                <p className="text-muted-foreground">
                  Bill documents, extracted data, processing metadata, and related workflow information 
                  uploaded or processed through our platform.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Usage and Technical Data</h4>
                <p className="text-muted-foreground">
                  Device information, IP addresses, browser details, usage patterns, and platform 
                  interaction logs for security and optimization purposes.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Communication Data</h4>
                <p className="text-muted-foreground">
                  Messages, support requests, feedback, and other communications with our team.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              How We Use Your Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold">Service Delivery</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Process and analyze bill documents</li>
                  <li>• Provide AI-powered data extraction</li>
                  <li>• Manage workflows and approvals</li>
                  <li>• Generate reports and analytics</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Platform Security</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Authenticate users and prevent fraud</li>
                  <li>• Monitor for security threats</li>
                  <li>• Audit access and usage</li>
                  <li>• Enforce access controls</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Communication</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Send platform notifications</li>
                  <li>• Provide customer support</li>
                  <li>• Share important updates</li>
                  <li>• Process access requests</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Improvement</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Optimize platform performance</li>
                  <li>• Enhance AI processing accuracy</li>
                  <li>• Develop new features</li>
                  <li>• Analyze usage patterns</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Sharing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Information Sharing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-success/10 p-4 rounded-lg border border-success/20">
              <p className="text-success text-sm font-medium">
                🔒 We do not sell, rent, or trade your personal information to third parties.
              </p>
            </div>
            
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Within Your Workspace</h4>
                <p className="text-muted-foreground">
                  Information is shared with other authorized members of your workspace according 
                  to your role and permissions.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Service Providers</h4>
                <p className="text-muted-foreground">
                  We work with trusted service providers (Supabase for infrastructure, AI processing 
                  services) who are bound by strict confidentiality agreements.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Legal Requirements</h4>
                <p className="text-muted-foreground">
                  We may disclose information when required by law, court order, or to protect 
                  our rights and security.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Data Retention & Deletion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold">Active Account Data</h4>
                <p className="text-muted-foreground text-sm">
                  We retain your data as long as your account is active and for legitimate 
                  business purposes such as compliance and security.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Account Deletion</h4>
                <p className="text-muted-foreground text-sm">
                  When you delete your account, we will delete your personal information 
                  within 30 days, except where retention is required by law.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Backup Data</h4>
                <p className="text-muted-foreground text-sm">
                  Backup copies may be retained for up to 90 days for disaster recovery 
                  purposes before permanent deletion.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Audit Logs</h4>
                <p className="text-muted-foreground text-sm">
                  Security and audit logs may be retained for up to 7 years for compliance 
                  and security investigation purposes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Your Rights (GDPR Compliance)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Under GDPR and other privacy laws, you have the following rights:</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold">Access & Portability</h4>
                <p className="text-muted-foreground text-sm">
                  Request a copy of your personal data and transfer it to another service.
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Correction</h4>
                <p className="text-muted-foreground text-sm">
                  Update or correct inaccurate personal information in your account.
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Deletion</h4>
                <p className="text-muted-foreground text-sm">
                  Request deletion of your personal data (subject to legal requirements).
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Processing Restriction</h4>
                <p className="text-muted-foreground text-sm">
                  Limit how we process your personal data in certain circumstances.
                </p>
              </div>
            </div>
            
            <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
              <p className="text-primary text-sm">
                To exercise these rights, contact us at <strong>privacy@billportal.ai</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Security Measures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold">Encryption</h4>
                <p className="text-muted-foreground text-sm">
                  All data is encrypted in transit (TLS 1.3) and at rest (AES-256).
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Access Controls</h4>
                <p className="text-muted-foreground text-sm">
                  Role-based permissions and multi-factor authentication requirements.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Monitoring</h4>
                <p className="text-muted-foreground text-sm">
                  24/7 security monitoring and incident response procedures.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Compliance</h4>
                <p className="text-muted-foreground text-sm">
                  SOC 2 Type II and GDPR compliance with regular security audits.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Email: <strong>privacy@billportal.ai</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Data Protection Officer: <strong>dpo@billportal.ai</strong></span>
              </div>
            </div>
            
            <Separator />
            
            <p className="text-xs text-muted-foreground">
              This Privacy Policy may be updated periodically. We will notify you of any material 
              changes via email or platform notification. Your continued use of BillPortal after 
              changes indicates your acceptance of the updated policy.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}